import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../../components/tour/TourContext';
import { navAnchor } from '../../components/tour/tourSteps';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
  useTheme,
  IconButton,
  Tooltip,
  SvgIcon,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SidebarContext } from "../../context/SidebarContext";
import { ColorModeContext } from "../../theme";
import { useAuth } from "../../contexts/AuthContext";
import { useFeatures } from "../../contexts/FeatureContext";
import { useVeraActivity } from "../vera/veraChatStore";
import {
  Code as CodeIcon,
  Dashboard as DashboardIcon,
  AccountTree as AccountTreeIcon,
  Computer as ComputerIcon,
  SmartToy as SmartToyIcon,
  AutoAwesome as AutoAwesomeIcon,
  ChatBubbleOutline as ChatIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Hub as HubIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Security as SecurityIcon,
  TourOutlined as TourIcon,
  Inventory2 as ResourcesIcon,
  Layers as InventoryIcon,
  Cloud as CloudIcon,
  HelpOutline as HelpOutlineIcon,
  ForumOutlined as ForumOutlinedIcon,
  MenuBook as MenuBookIcon,
  Campaign as OnCallIcon,
} from '@mui/icons-material';

const KubernetesIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 2c4.41 0 8 3.59 8 8s-3.59 8-8 8-8-3.59-8-8 3.59-8 8-8zm0 2.5c-3.04 0-5.5 2.46-5.5 5.5s2.46 5.5 5.5 5.5 5.5-2.46 5.5-5.5-2.46-5.5-5.5-5.5zm0 2c1.93 0 3.5 1.57 3.5 3.5s-1.57 3.5-3.5 3.5-3.5-1.57-3.5-3.5 1.57-3.5 3.5-3.5zm0-4.5v2m0 9v2M4.93 7.5l1.73 1M17.34 15.5l1.73 1M4.93 16.5l1.73-1M17.34 8.5l1.73-1M7 12H5m14 0h-2"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.5"
    />
  </SvgIcon>
);

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 56;

const menuGroups = [
  {
    title: 'Overview',
    items: [
      { title: "Home",       path: "/",          icon: <HomeIcon /> },
      { title: "Dashboards", path: "/dashboards", icon: <DashboardIcon /> },
    ],
  },
  {
    title: 'Monitor',
    items: [
      { title: "Applications", path: "/applications", icon: <CodeIcon /> },
      { title: "Infrastructure", path: "/infrastructure", icon: <ComputerIcon /> },
      { title: "Kubernetes",  path: "/kubernetes",    icon: <KubernetesIcon /> },
      { title: "Network",     path: "/network",       icon: <HubIcon /> },
      { title: "Alerts",      path: "/alerts",        icon: <NotificationsIcon /> },
    ],
  },
  {
    title: 'Analyze',
    items: [
      { title: "Inventory",     path: "/inventory",      icon: <InventoryIcon />, feature: 'inventory' },
      { title: "Synthetics",    path: "/synthetic",      icon: <SmartToyIcon /> },
      { title: "Workbooks",     path: "/workbooks",      icon: <AccountTreeIcon />, feature: 'workbooks' },
      { title: "Web Experience",path: "/web-experience", icon: <LanguageIcon /> },
      { title: "Data Explorer", path: "/data-explorer",  icon: <ScienceIcon /> },
    ],
  },
  {
    title: 'AI & Automation',
    items: [
      { title: "Vera", path: "/vera", icon: <ChatIcon />, feature: 'vera' },
      { title: "Sentinel", path: "/sentinel", icon: <AutoAwesomeIcon />, feature: 'sentinel' },
    ],
  },
  {
    title: 'Manage',
    items: [
      { title: "Response",  path: "/response",  icon: <OnCallIcon /> },
      { title: "Cloud Integrations", path: "/integrations", icon: <CloudIcon />, adminOnly: true },
      { title: "Resources", path: "/resources", icon: <ResourcesIcon /> },
      // Settings is the ONE nav area regular users don't get (role model:
      // USER = everything except Settings; ADMIN = USER + Settings).
      { title: "Settings",  path: "/settings",  icon: <SettingsIcon />, adminOnly: true },
    ],
  },
  {
    title: 'Help & Contact',
    items: [
      { title: "Documentation", path: "https://verops.io/docs", external: true, icon: <MenuBookIcon /> },
      { title: "Contact us",    path: "/contact/new", icon: <HelpOutlineIcon /> },
      { title: "My tickets",    path: "/contact",     icon: <ForumOutlinedIcon /> },
    ],
  },
];

const GlobalSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const { isFeatureEnabled } = useFeatures();
  // Vera background-chat activity: identity only changes when the flags flip,
  // so streaming tokens don't re-render the whole nav.
  const veraActivity = useVeraActivity();
  const { isSidebar, setIsSidebar } = useContext(SidebarContext);
  // The product tour points at sidebar items: while it runs the sidebar is open and every
  // group expanded, so no stop is hidden inside a collapsed group.
  const { open: touring, start: startTour } = useTour();
  useEffect(() => { if (touring && !isSidebar) setIsSidebar(true); }, [touring]); // eslint-disable-line react-hooks/exhaustive-deps
  const collapsed = !isSidebar;
  const setCollapsed = (v) => setIsSidebar(!v);

  const [expandedGroups, setExpandedGroups] = useState({
    Overview: true,
    Monitor: true,
    Analyze: true,
    'AI & Automation': true,
    Manage: true,
    'Help & Contact': false, // collapsed by default to keep the nav scroll-free
  });
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleGroup = (title) =>
    setExpandedGroups((prev) => ({ ...prev, [title]: !prev[title] }));

  const handleProfileOpen = (e) => setProfileAnchorEl(e.currentTarget);
  const handleProfileClose = () => setProfileAnchorEl(null);
  const handleLogout = () => { logout(); handleProfileClose(); };

  const currentWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  // Sidebar background: subtle gradient for depth
  const sidebarBg = isDark
    ? 'linear-gradient(180deg, #0d1117 0%, #0a0d12 100%)'
    : '#ffffff';

  return (
    <Drawer
      variant="permanent"
      PaperProps={{ 'data-tour': 'sidebar' }}
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          background: sidebarBg,
          borderRight: isDark
            ? '1px solid rgba(255,255,255,0.07)'
            : `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Logo ───────────────────────────────────────────────── */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 0 : 1.5,
          minHeight: 52,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: isDark
            ? '1px solid rgba(255,255,255,0.07)'
            : `1px solid ${theme.palette.divider}`,
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0,
            boxShadow: isDark
              ? '0 0 0 1px rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.4)'
              : '0 1px 4px rgba(0,0,0,0.12)',
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo512.png`}
            alt="VerOps"
            style={{ width: '100%', height: '100%', display: 'block' }}
          />
        </Box>
        {!collapsed && (
          <Box sx={{ ml: 1, display: 'flex', alignItems: 'baseline', gap: '1px' }}>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 800,
                color: primary,
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              V
            </Typography>
            <Typography
              sx={{
                fontSize: '0.88rem',
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}
            >
              erOps
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Navigation ─────────────────────────────────────────── */}
      <Box sx={{ overflow: 'auto', py: 0.5, flexGrow: 1,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          borderRadius: 2,
        },
      }}>
        {menuGroups.map((group, groupIndex) => (
          <Box key={group.title} sx={{ mb: 0.125 }}>
            {/* Section divider between groups (not before the first) */}
            {groupIndex > 0 && (
              <Box
                sx={{
                  mx: 1.5,
                  my: 0.4,
                  height: '1px',
                  background: isDark
                    ? 'rgba(255,255,255,0.07)'
                    : 'rgba(0,0,0,0.07)',
                }}
              />
            )}

            {/* Section header */}
            {!collapsed && (
              <Box
                onClick={() => toggleGroup(group.title)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mx: 1.25,
                  mb: 0.125,
                  px: 0.5,
                  py: 0.25,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.04)'
                      : 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    // WCAG 2.1 AA contrast. These section headers were rgba(0,0,0,0.38),
                    // which renders as #9e9e9e on white — a measured 2.67:1 against the
                    // required 4.5:1, at 8px. axe-core flagged it on every page of the
                    // application, not only Sentinel's; it is the single most widespread
                    // accessibility defect in the product.
                    //
                    // 0.6 alpha on white is ~#666666 (5.7:1) and on the dark surface 0.55
                    // clears the threshold too, so both themes pass without changing the
                    // visual hierarchy these headers exist to create — they still read as
                    // quieter than the items beneath them.
                    color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.6)',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    userSelect: 'none',
                  }}
                >
                  {group.title}
                </Typography>
                <Box
                  sx={{
                    color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'transform 0.2s ease',
                    transform: expandedGroups[group.title] ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 13 }} />
                </Box>
              </Box>
            )}

            <Collapse
              in={!collapsed ? (expandedGroups[group.title] || (touring && group.title !== 'Help & Contact')) : true}
              timeout={200}
              unmountOnExit
            >
              <List disablePadding sx={{ px: 0.75 }}>
                {group.items
                  .filter((item) => !item.superAdminOnly || isSuperAdmin)
                  .filter((item) => !item.adminOnly || isAdmin)
                  .filter((item) => !item.feature || isFeatureEnabled(item.feature))
                  .map((item) => {
                    const active = isActive(item.path);
                    // Vera nav nudges: a finished answer the user hasn't seen
                    // yet glows; an in-flight answer softly pulses. Neither
                    // shows while the user is already on Vera's screen.
                    const isVeraItem = item.path === '/vera';
                    const veraDone = isVeraItem && veraActivity.unseenDone && !active;
                    const veraWorking = isVeraItem && veraActivity.busy && !active && !veraDone;
                    return (
                      <ListItem key={item.title} disablePadding sx={{ mb: 0.125 }}>
                        <Tooltip
                          title={collapsed ? item.title : ''}
                          placement="right"
                          disableHoverListener={!collapsed}
                        >
                          <ListItemButton
                            data-tour={navAnchor(item.path)}
                            selected={active}
                            onClick={() => (item.external ? window.open(item.path, '_blank', 'noopener') : navigate(item.path))}
                            sx={{
                              borderRadius: '8px',
                              minHeight: 31,
                              py: 0,
                              justifyContent: collapsed ? 'center' : 'flex-start',
                              px: collapsed ? 0 : 0.75,
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'background-color 0.15s ease',
                              '&.Mui-selected': {
                                backgroundColor: isDark
                                  ? alpha(primary, 0.12)
                                  : alpha(primary, 0.08),
                                '&:hover': {
                                  backgroundColor: isDark
                                    ? alpha(primary, 0.18)
                                    : alpha(primary, 0.12),
                                },
                                // Left accent bar
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: '18%',
                                  bottom: '18%',
                                  width: 3,
                                  backgroundColor: primary,
                                  borderRadius: '0 4px 4px 0',
                                  boxShadow: isDark
                                    ? `0 0 8px ${alpha(primary, 0.6)}`
                                    : 'none',
                                },
                              },
                              '&:hover': {
                                backgroundColor: isDark
                                  ? 'rgba(255,255,255,0.05)'
                                  : 'rgba(0,0,0,0.04)',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: collapsed ? 'auto' : 30,
                                justifyContent: 'center',
                                color: 'inherit',
                              }}
                            >
                              {/* Icon bubble */}
                              <Box
                                sx={{
                                  width: 25,
                                  height: 25,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '7px',
                                  position: 'relative',
                                  backgroundColor: active
                                    ? isDark
                                      ? alpha(primary, 0.18)
                                      : alpha(primary, 0.1)
                                    : 'transparent',
                                  color: active
                                    ? primary
                                    : theme.palette.text.secondary,
                                  transition: 'background-color 0.15s ease, color 0.15s ease',
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 16,
                                    transition: 'filter 0.2s ease',
                                    filter: active && isDark
                                      ? `drop-shadow(0 0 5px ${alpha(primary, 0.55)})`
                                      : 'none',
                                  },
                                  ...(veraDone && {
                                    color: primary,
                                    animation: 'veraNavGlow 1.4s ease-in-out infinite',
                                    '@keyframes veraNavGlow': {
                                      '0%, 100%': {
                                        transform: 'scale(1)',
                                        boxShadow: `0 0 0 0 ${alpha(primary, 0)}`,
                                      },
                                      '50%': {
                                        transform: 'scale(1.14)',
                                        boxShadow: `0 0 10px 1px ${alpha(primary, 0.45)}`,
                                      },
                                    },
                                  }),
                                  ...(veraWorking && {
                                    animation: 'veraNavThink 1.8s ease-in-out infinite',
                                    '@keyframes veraNavThink': {
                                      '0%, 100%': { opacity: 0.45 },
                                      '50%': { opacity: 1 },
                                    },
                                  }),
                                }}
                              >
                                {item.icon}
                                {veraDone && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      bgcolor: 'success.main',
                                      boxShadow: `0 0 6px ${alpha(theme.palette.success.main, 0.9)}`,
                                      animation: 'veraNavDot 1.4s ease-in-out infinite',
                                      '@keyframes veraNavDot': {
                                        '0%, 100%': { transform: 'scale(1)' },
                                        '50%': { transform: 'scale(1.4)' },
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </ListItemIcon>

                            {!collapsed && (
                              <ListItemText
                                primary={item.title}
                                primaryTypographyProps={{
                                  fontSize: '0.8rem',
                                  fontWeight: active ? 600 : 400,
                                  color: active ? primary : 'text.primary',
                                  letterSpacing: active ? '-0.01em' : 'normal',
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* ── Profile ────────────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            mx: 0.75,
            mb: 0.5,
            borderTop: isDark
              ? '1px solid rgba(255,255,255,0.07)'
              : `1px solid ${theme.palette.divider}`,
            pt: 0.5,
          }}
        >
          <Box
            data-tour="profile"
            onClick={handleProfileOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: collapsed ? 0 : 0.75,
              py: 0.625,
              borderRadius: '8px',
              cursor: 'pointer',
              justifyContent: collapsed ? 'center' : 'flex-start',
              transition: 'background-color 0.15s ease',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <Tooltip
              title={collapsed ? (user?.username || 'Profile') : ''}
              placement="right"
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  // Full-strength primary rather than 85% alpha: blending toward the page
                  // background lifted the effective background to #467aee and left the
                  // initials at a measured 3.8:1, under the 4.5:1 minimum. At full opacity
                  // the same foreground clears it, and the avatar looks the same to anyone
                  // not measuring it.
                  backgroundColor: primary,
                  color: theme.palette.getContrastText(primary),
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: isDark
                    ? `0 0 0 2px rgba(255,255,255,0.07), 0 0 8px ${alpha(primary, 0.35)}`
                    : `0 0 0 2px rgba(0,0,0,0.06)`,
                }}
              >
                {user?.username?.substring(0, 2).toUpperCase() || 'U'}
              </Avatar>
            </Tooltip>

            {!collapsed && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '0.775rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    {user?.username || 'User'}
                  </Typography>
                  {isSuperAdmin && (
                    <Tooltip title="Operator Account" placement="top" arrow>
                      <SecurityIcon
                        sx={{ fontSize: 12, color: theme.palette.warning.main, flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.64rem',
                    // `text.disabled` resolves to #94a3b8, a measured 2.56:1 on white
                    // against the 4.5:1 minimum. This is the organization NAME — the one
                    // piece of text that tells a user which tenant they are about to act
                    // in — so rendering it below the legibility threshold is a correctness
                    // problem before it is an accessibility one.
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                  }}
                >
                  {user?.orgName || user?.orgSlug || user?.email || ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Dark mode + collapse */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            px: 0.75,
            py: 0.5,
            borderTop: isDark
              ? '1px solid rgba(255,255,255,0.07)'
              : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tooltip
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            placement="right"
          >
            <IconButton
              onClick={colorMode.toggleColorMode}
              size="small"
              sx={{
                color: 'text.disabled',
                padding: 0.625,
                borderRadius: '7px',
                transition: 'background-color 0.15s ease, color 0.15s ease',
                '&:hover': {
                  color: 'text.secondary',
                  backgroundColor: isDark
                    ? 'rgba(255,255,255,0.07)'
                    : 'rgba(0,0,0,0.06)',
                },
                '& .MuiSvgIcon-root': { fontSize: 15 },
              }}
            >
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {!collapsed && (
            <Tooltip title="Collapse sidebar" placement="right">
              <IconButton
                size="small"
                onClick={() => setCollapsed(!collapsed)}
                aria-label="Collapse sidebar"
                sx={{
                  color: 'text.disabled',
                  padding: 0.625,
                  borderRadius: '7px',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  '&:hover': {
                    color: 'text.secondary',
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.07)'
                      : 'rgba(0,0,0,0.06)',
                  },
                  '& .MuiSvgIcon-root': { fontSize: 15 },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          )}

          {collapsed && (
            <Tooltip title="Expand sidebar" placement="right">
              <IconButton
                size="small"
                onClick={() => setCollapsed(!collapsed)}
                aria-label="Expand sidebar"
                sx={{
                  color: 'text.disabled',
                  padding: 0.625,
                  borderRadius: '7px',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  '&:hover': {
                    color: 'text.secondary',
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.07)'
                      : 'rgba(0,0,0,0.06)',
                  },
                  '& .MuiSvgIcon-root': { fontSize: 15 },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Profile popup menu ─────────────────────────────────── */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 230,
            borderRadius: '12px',
            ml: 1,
            border: isDark
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.08)',
            backgroundImage: 'none',
            backgroundColor: isDark ? '#161b22' : '#ffffff',
          },
        }}
      >
        {/* Profile header */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                backgroundColor: alpha(primary, 0.85),
                fontSize: '0.8rem',
                fontWeight: 700,
                boxShadow: `0 0 0 2px ${alpha(primary, 0.3)}`,
              }}
            >
              {user?.username?.substring(0, 2).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  sx={{ fontSize: '0.825rem', fontWeight: 700, lineHeight: 1.3 }}
                  noWrap
                >
                  {user?.username || 'User'}
                </Typography>
                {isAdmin && (
                  <Chip
                    label={isSuperAdmin ? 'Super Admin' : 'Admin'}
                    size="small"
                    sx={{
                      height: 17,
                      fontSize: '0.58rem',
                      fontWeight: 700,
                      backgroundColor: alpha(primary, 0.15),
                      color: primary,
                      border: 'none',
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{ fontSize: '0.675rem', color: 'text.secondary', lineHeight: 1.3 }}
                noWrap
              >
                {user?.email || ''}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mx: 1.5,
            mb: 0.5,
            height: '1px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
          }}
        />

        <Box sx={{ px: 0.75, py: 0.5 }}>
          {[
            { label: 'Profile',  icon: <AccountCircleIcon sx={{ fontSize: 16 }} />, path: '/profile' },
            // Settings entry mirrors the nav: admins only.
            ...(isAdmin ? [{ label: 'Settings', icon: <SettingsIcon sx={{ fontSize: 16 }} />, path: '/settings' }] : []),
            { label: 'Security', icon: <SecurityIcon     sx={{ fontSize: 16 }} />, path: '/profile' },
            { label: 'Product tour', icon: <TourIcon sx={{ fontSize: 16 }} />, action: startTour },
          ].map(({ label, icon, path, action }) => (
            <MenuItem
              key={label}
              onClick={() => { if (action) action(); else navigate(path); handleProfileClose(); }}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: '7px',
                gap: 1.25,
                transition: 'background-color 0.12s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 400 }}>{label}</Typography>
            </MenuItem>
          ))}
        </Box>

        <Box
          sx={{
            mx: 1.5,
            my: 0.5,
            height: '1px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
          }}
        />

        <Box sx={{ px: 0.75, pb: 0.75 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: '7px',
              gap: 1.25,
              color: 'error.main',
              transition: 'background-color 0.12s ease',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(248,81,73,0.09)' : 'rgba(209,36,47,0.06)',
              },
            }}
          >
            <LogoutIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.8rem' }}>Sign out</Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Drawer>
  );
};

export default GlobalSidebar;
